import { Box, Divider, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationResultSurface } from "src/features/application-result-referral/experiment";
import useReferralLink from "src/features/referrals/hooks/useReferralLink";
import buildReferralShareLinks from "src/features/referrals/utils/buildReferralShareLinks";
import useReferralCode from "src/hooks/data/useReferralCode";
import { trackEvent } from "src/hooks/useInstrumentation";
import { useCurrentUser } from "src/SessionBoundary";
import Button from "src/shared/components/Button";
import { referralQuestion } from "../copy";

type Props = {
  surface: ApplicationResultSurface;
  /** One short sentence under the headline. */
  reason: string;
  /** Quieter second line; may carry an inline link. */
  followUp?: ReactNode;
  /** Called after "Send my link" or "Not now". */
  onDone: () => void;
};

/**
 * The referral ask shown right after an application result, in the test arm
 * of `application-result-referral-ask`. One action: text a friend the
 * student's referral link. "Not now" moves on without it.
 */
export default function ReferralAskScreen({
  surface,
  reason,
  followUp,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const { data, loading, refetch } = useReferralCode();
  const { referralLink, shareText } = useReferralLink(data?.code ?? "");
  const smsHref = buildReferralShareLinks({ referralLink, shareText }).sms;

  const ready = Boolean(data);
  const failed = !loading && !data;

  useEffect(() => {
    trackEvent("Application Result Referral Ask - Viewed", { surface });
  }, [surface]);

  const handleSend = () => {
    // The button's `href` opens the SMS composer; we only record the intent.
    trackEvent("Application Result Referral Ask - Share Opened", {
      surface,
      method: "sms",
    });
    // Move on after the browser has followed the link, so the re-render that
    // removes this button can't race the sms: navigation.
    window.setTimeout(onDone, 0);
  };

  const handleNotNow = () => {
    trackEvent("Application Result Referral Ask - Dismissed", {
      surface,
      hadLink: ready,
    });
    onDone();
  };

  return (
    <Box
      component="section"
      aria-busy={loading}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100dvh",
        width: "100%",
        maxWidth: 480,
        mx: "auto",
        // The app has no global border-box reset, so width:100% + px:3 would
        // lay this out 48px wider than the viewport: the brown card clips, the
        // page scrolls sideways, and inside the success-plan StoryViewer the
        // extra width widens the next slide too.
        boxSizing: "border-box",
        px: 3,
        pt: 6,
        pb: "calc(env(safe-area-inset-bottom) + 24px)",
      }}
    >
      <Stack spacing={2}>
        <Typography component="h1" variant="h1">
          {t("Get a friend free training.")}
        </Typography>
        <Typography>{reason}</Typography>
        {followUp && (
          <Typography component="div" color="text.secondary">
            {followUp}
          </Typography>
        )}
      </Stack>

      <Divider sx={{ my: 4 }} />

      <Box
        component="section"
        sx={(theme) => ({
          p: 3,
          borderRadius: 2,
          bgcolor: theme.palette.brown,
          color: theme.palette.common.white,
        })}
      >
        <Typography component="h2" variant="h2" sx={{ color: "inherit" }}>
          {referralQuestion(t, user)}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="baseline" mt={2}>
          <Typography
            component="p"
            variant="h1"
            sx={{ color: "inherit", flexShrink: 0 }}
          >
            {t("$150")}
          </Typography>
          <Typography
            variant="body2"
            sx={(theme) => ({
              color: alpha(theme.palette.common.white, 0.7),
              minWidth: 0,
            })}
          >
            {t("for you when they get their permit.")}
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 32 }} />

      <Stack spacing={1}>
        {failed ? (
          <Stack spacing={1} alignItems="center" role="alert">
            <Typography variant="body2" textAlign="center">
              {t("We couldn't load your link.")}
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => refetch()}
              sx={{ minHeight: 48 }}
            >
              {t("Try again")}
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            size="large"
            fullWidth
            href={ready ? smsHref : undefined}
            onClick={handleSend}
            disabled={!ready}
            sx={{ minHeight: 48 }}
          >
            {t("Send my link")}
          </Button>
        )}
        <Button
          variant="text"
          color="inherit"
          fullWidth
          onClick={handleNotNow}
          sx={{ minHeight: 44 }}
        >
          {t("Not now")}
        </Button>
      </Stack>
    </Box>
  );
}
